/* -------------------------- Design imports start -------------------------- */
import { CloseRounded, TuneRounded } from '@mui/icons-material'
import {
  Box,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import {
  HoopsModelTree,
  type ModelTree,
  type ModelTreeNodeClickEvent,
  type ModelTreeNodeVisibilityClickEvent,
} from '@hoops/web-viewer-components'
import type * as Communicator from '@hoops/web-viewer'
import { type MutableRefObject } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '../Button'
import { type HoopsCADViewerSidebarProps, type ViewerSetting } from './types'
/* ------------------------- Functional imports end ------------------------- */

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function HoopsCADViewerSidebar(props: HoopsCADViewerSidebarProps): JSX.Element {
  const {
    modelTree,
    modelName,
    settings,
    settingsState,
    onSettingsChange,
    onNodeClick,
    onVisibilityChange,
    openSettings,
    onToggleSettings,
    webViewer,
  } = props

  const { t } = useTranslation()

  const handleTreeNodeClick = (event: ModelTreeNodeClickEvent): void => {
    onNodeClick(event)
  }

  const handleTreeNodeVisibilityChange = (event: ModelTreeNodeVisibilityClickEvent): void => {
    onVisibilityChange(event)
  }

  return (
    <Box display="flex" flexDirection="column" height="100%" borderRight="1px solid lightGray">
      <Box id="CADViewerModelInfoContainer" flexGrow={1} display="flex" flexDirection="column">
        <Typography variant="h5" p="16px" pb="0px" mb="16px">
          {modelName ? `Model: ${modelName}` : 'Model'}
        </Typography>
        <Box id="modelTreeContainer" flexGrow={1} flexBasis={0} overflow="hidden">
          <Box id="modelTreeScrollContainer" p="0px 16px" height="100%" overflow="auto">
            <Box id="modelTree">
              <HoopsModelTree
                id="hwvModelTree"
                ref={modelTree}
                modelTreeNodeClick={handleTreeNodeClick}
                modelTreeNodeVisibilityChange={handleTreeNodeVisibilityChange}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <Box id="CADViewerSettingsContainer">
        {openSettings && (
          <List sx={{ borderTop: '1px solid #e9ecef' }} disablePadding>
            {settings.map(setting => {
              const settingLabelId = setting.key + 'label'
              return (
                <ListItem key={setting.key} disablePadding>
                  <ListItemButton
                    role={undefined}
                    onClick={() => onSettingsChange(setting.key, !settingsState.includes(setting.key))}
                    dense
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={settingsState.includes(setting.key)}
                        disableRipple
                        aria-labelledby={settingLabelId}
                      />
                    </ListItemIcon>
                    <ListItemText id={settingLabelId} primary={setting.label} />
                  </ListItemButton>
                </ListItem>
              )
            })}
          </List>
        )}
        <Box display="flex" justifyContent="center" p="16px">
          <Button
            disabled={webViewer.current === null}
            variant="outlined"
            color="inherit"
            startIcon={openSettings ? <CloseRounded /> : <TuneRounded />}
            onClick={onToggleSettings}
          >
            {t('common:content.label.settings')}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
